"use client";
import React, { forwardRef, useState } from "react";
import { GlassButton, IconButton } from "@/components/button";
import { cn } from "../../../lib/utilsComprehensive";
import { GlassCard } from "@/components/card";
import { GlassBadge } from "@/components/data-display";
import { GlassGrid, GlassGridItem } from "@/components/layout";
import { HStack, VStack } from "@/components/layout";
import { Motion } from "@/primitives";

export interface DetailField {
  id: string;
  label: string;
  value: unknown;
  type?: "text" | "number" | "date" | "boolean" | "badge" | "link" | "custom";
  render?: (value: unknown) => React.ReactNode;
  copyable?: boolean;
  width?: "auto" | "full" | "half" | "third" | "quarter";
}

export interface DetailSection {
  id: string;
  title: string;
  description?: string;
  fields?: DetailField[];
  component?: React.ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

export interface DetailTab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  badge?: string | number;
  content: React.ReactNode;
}

export interface DetailAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  variant?: "default" | "primary" | "secondary" | "error";
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

export interface GlassDetailViewProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Detail item title
   */
  title?: string;
  /**
   * Detail item subtitle
   */
  subtitle?: string;
  /**
   * Status badge
   */
  status?: {
    label: string;
    variant?:
      | "default"
      | "primary"
      | "secondary"
      | "success"
      | "warning"
      | "error";
  };
  /**
   * Breadcrumb navigation
   */
  breadcrumb?: React.ReactNode;
  /**
   * Header actions
   */
  actions?: DetailAction[];
  /**
   * Detail sections
   */
  sections?: DetailSection[];
  /**
   * Detail tabs
   */
  tabs?: DetailTab[];
  /**
   * Active tab
   */
  activeTab?: string;
  /**
   * Tab change handler
   */
  onTabChange?: (tabId: string) => void;
  /**
   * Layout variant
   */
  layout?: "default" | "sidebar" | "tabs" | "accordion";
  /**
   * Sidebar content (for sidebar layout)
   */
  sidebar?: React.ReactNode;
  /**
   * Loading state
   */
  loading?: boolean;
  /**
   * Error state
   */
  error?: string;
  /**
   * Back action
   */
  onBack?: () => void;
  /**
   * Share action
   */
  onShare?: () => void;
  /**
   * Print action
   */
  onPrint?: () => void;
  /**
   * Compact preview/card layout.
   */
  compact?: boolean;
  /**
   * Bound the detail view inside its parent container.
   */
  contained?: boolean;
  /**
   * Maximum height for compact/contained detail views.
   */
  maxHeight?: number | string;
}

/**
 * GlassDetailView component
 * Comprehensive detail view with multiple layout options
 */
export const GlassDetailView = forwardRef<HTMLDivElement, GlassDetailViewProps>(
  (
    {
      title = "Detail View",
      subtitle,
      status,
      breadcrumb,
      actions = [],
      sections = [],
      tabs = [],
      activeTab,
      onTabChange,
      layout = "default",
      sidebar,
      loading = false,
      error,
      onBack,
      onShare,
      onPrint,
      compact = false,
      contained = false,
      maxHeight,
      className,
      children,
      "aria-label": ariaLabel,
      "data-testid": dataTestId,
      ...props
    },
    ref
  ) => {
    const stringifyFieldValue = (value: unknown): string => {
      if (value == null) return "";
      if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
      ) {
        return String(value);
      }
      if (value instanceof Date) {
        return value.toISOString();
      }
      return JSON.stringify(value);
    };

    const [expandedSections, setExpandedSections] = useState<Set<string>>(
      new Set(
        sections.filter((s) => s.defaultExpanded !== false).map((s) => s.id)
      )
    );
    const [currentTab, setCurrentTab] = useState(activeTab || tabs[0]?.id);
    const boundedHeight = maxHeight ?? (compact || contained ? 260 : undefined);

    // Toggle section expansion
    const toggleSection = (sectionId: string) => {
      const newExpanded = new Set(expandedSections);
      if (newExpanded.has(sectionId)) {
        newExpanded.delete(sectionId);
      } else {
        newExpanded.add(sectionId);
      }
      setExpandedSections(newExpanded);
    };

    // Handle tab change
    const handleTabChange = (tabId: string) => {
      setCurrentTab(tabId);
      onTabChange?.(tabId);
    };

    // Render field value
    const renderFieldValue = (field: DetailField) => {
      if (field.render) {
        return field.render(field.value);
      }

      switch (field.type) {
        case "boolean":
          return (
            <GlassBadge
              data-glass-component
              variant={Boolean(field.value) ? "success" : "error"}
              size="xs"
            >
              {Boolean(field.value) ? "Yes" : "No"}
            </GlassBadge>
          );
        case "badge":
          return (
            <GlassBadge variant="outline" size="xs">
              {stringifyFieldValue(field.value)}
            </GlassBadge>
          );
        case "link":
          const href = stringifyFieldValue(field.value);
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="glass-text-primary hover:glass-underline glass-focus glass-touch-target glass-contrast-guard"
            >
              {href}
            </a>
          );
        case "date":
          return new Date(
            stringifyFieldValue(field.value)
          ).toLocaleDateString();
        case "number":
          return typeof field.value === "number"
            ? field.value.toLocaleString()
            : stringifyFieldValue(field.value);
        default:
          return stringifyFieldValue(field.value);
      }
    };

    // Render field
    const renderField = (field: DetailField) => {
      const widthClasses = {
        auto: "w-auto",
        full: "w-full",
        half: "w-1/2",
        third: "w-1/3",
        quarter: "w-1/4",
      };

      return (
        <div
          key={field.id}
          className={cn(
            "glass-auto-gap glass-auto-gap-xs",
            widthClasses[field.width || "auto"]
          )}
        >
          <label className="glass-text-sm glass-font-medium glass-text-secondary">
            {field.label}
          </label>
          <HStack space="sm" align="center">
            <div className="glass-text-sm glass-text-primary">
              {renderFieldValue(field)}
            </div>
            {field.copyable && (
              <IconButton
                icon="📋"
                variant="ghost"
                size="xs"
                onClick={(e) =>
                  navigator.clipboard?.writeText(String(field.value))
                }
                aria-label="Copy to clipboard"
                className="glass-focus glass-touch-target"
              />
            )}
          </HStack>
        </div>
      );
    };

    // Render section
    const renderSection = (section: DetailSection) => {
      const isExpanded = expandedSections.has(section.id);

      return (
        <Motion key={section.id} type="slide" direction="down">
          <GlassCard
            variant="default"
            className={compact ? "glass-p-3" : undefined}
          >
            {/* Section header */}
            <div
              className={cn(
                "flex items-center justify-between",
                section.collapsible &&
                  "cursor-pointer glass-focus glass-touch-target"
              )}
              onClick={
                section.collapsible
                  ? () => toggleSection(section.id)
                  : undefined
              }
            >
              <VStack space="xs">
                <h3 className="glass-text-lg glass-font-semibold glass-text-primary">
                  {section.title}
                </h3>
                {section.description && (
                  <p className="glass-text-sm glass-text-secondary">
                    {section.description}
                  </p>
                )}
              </VStack>

              {section.collapsible && (
                <GlassButton
                  leftIcon={isExpanded ? "−" : "+"}
                  variant="ghost"
                  size="sm"
                  aria-label={
                    isExpanded ? "Collapse section" : "Expand section"
                  }
                />
              )}
            </div>

            {/* Section content */}
            {(!section.collapsible || isExpanded) && (
              <Motion type="slide" direction="down" className="glass-mt-6">
                {section.component ? (
                  section.component
                ) : section.fields ? (
                  <div
                    className={cn(
                      "glass-grid glass-grid-cols-1",
                      compact
                        ? "glass-gap-3"
                        : "md:glass-grid-cols-2 lg:glass-grid-cols-3 glass-gap-6"
                    )}
                  >
                    {(compact
                      ? section.fields.slice(0, 4)
                      : section.fields
                    ).map(renderField)}
                  </div>
                ) : null}
              </Motion>
            )}
          </GlassCard>
        </Motion>
      );
    };

    // Render tabs
    const renderTabs = () => {
      if (tabs.length === 0) return null;

      return (
        <VStack space="md">
          {/* Tab navigation */}
          <div className="glass-border-b glass-border-glass-border/20">
            <nav className="glass-flex glass-space-x-8">
              {tabs.map((tab) => (
                <GlassButton
                  key={tab.id}
                  onClick={(e) => handleTabChange(tab.id)}
                  className={cn(
                    "flex items-center glass-gap-2 glass-py-2 glass-px-1 border-b-2 transition-colors glass-focus glass-touch-target",
                    currentTab === tab.id
                      ? "border-primary text-primary font-medium"
                      : "border-transparent glass-text-secondary hover:text-foreground"
                  )}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                  {tab.badge && (
                    <GlassBadge variant="outline" size="xs">
                      {tab.badge}
                    </GlassBadge>
                  )}
                </GlassButton>
              ))}
            </nav>
          </div>

          {/* Tab content */}
          <div>{tabs.find((tab) => tab.id === currentTab)?.content}</div>
        </VStack>
      );
    };

    // Render header
    const renderHeader = () => (
      <VStack space="md">
        {/* Breadcrumb */}
        {breadcrumb && (
          <div className="glass-text-sm glass-text-secondary">{breadcrumb}</div>
        )}

        {/* Title section */}
        <HStack space="md" align="center" justify="between">
          <HStack space="md" align="center">
            {onBack && (
              <GlassButton
                leftIcon="←"
                variant="ghost"
                size="sm"
                onClick={onBack}
                aria-label="Go back"
                className="glass-focus glass-touch-target"
              />
            )}

            <VStack space="xs">
              <HStack space="sm" align="center">
                <h1 className="glass-text-2xl glass-font-bold glass-text-primary">
                  {title}
                </h1>
                {status && (
                  <GlassBadge variant={status.variant} size="sm">
                    {status.label}
                  </GlassBadge>
                )}
              </HStack>

              {subtitle && (
                <p className="glass-text-lg glass-text-secondary">{subtitle}</p>
              )}
            </VStack>
          </HStack>

          {/* Actions */}
          <HStack space="sm" align="center">
            {!compact && onShare && (
              <GlassButton
                leftIcon="📤"
                variant="ghost"
                size="sm"
                onClick={onShare}
                aria-label="Share"
                className="glass-focus glass-touch-target"
              />
            )}

            {!compact && onPrint && (
              <GlassButton
                leftIcon="🖨️"
                variant="ghost"
                size="sm"
                onClick={onPrint}
                aria-label="Print"
                className="glass-focus glass-touch-target"
              />
            )}

            {!compact &&
              actions.map((action) => (
                <GlassButton
                  key={action.id}
                  variant={action.variant}
                  size="sm"
                  leftIcon={action.icon}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  loading={action.loading}
                  className="glass-focus glass-touch-target"
                >
                  {action.label}
                </GlassButton>
              ))}
          </HStack>
        </HStack>
      </VStack>
    );

    // Render content based on layout
    const renderContent = () => {
      if (loading) {
        return (
          <div className="glass-flex glass-items-center glass-justify-center glass-h-64">
            <div className="glass-w-8 glass-h-8 glass-border-2 glass-border-primary glass-border-t-transparent glass-radius-full glass-animate-spin" />
          </div>
        );
      }

      if (error) {
        return (
          <GlassCard variant="default" className="glass-p-8 glass-text-center">
            <div className="glass-text-danger glass-font-medium glass-mb-2">
              Error
            </div>
            <div className="glass-text-secondary">{error}</div>
          </GlassCard>
        );
      }

      switch (layout) {
        case "sidebar":
          return (
            <GlassGrid cols={12} gap="lg">
              <GlassGridItem colSpan={8}>
                <VStack space="lg">
                  {tabs.length > 0 ? renderTabs() : sections.map(renderSection)}
                  {children}
                </VStack>
              </GlassGridItem>
              <GlassGridItem colSpan={4}>{sidebar}</GlassGridItem>
            </GlassGrid>
          );

        case "tabs":
          return renderTabs();

        case "accordion":
          return (
            <VStack space="md">
              {sections
                .map((section) => ({
                  ...section,
                  collapsible: true,
                }))
                .map(renderSection)}
              {children}
            </VStack>
          );

        default:
          return (
            <VStack space="lg">
              {sections.map(renderSection)}
              {tabs.length > 0 && renderTabs()}
              {children}
            </VStack>
          );
      }
    };

    return (
      <div
        ref={ref}
        className={cn(
          "w-full",
          compact
            ? "glass-auto-gap glass-auto-gap-md"
            : "glass-auto-gap glass-auto-gap-3xl",
          (compact || contained) && "glass-overflow-auto",
          className
        )}
        style={{
          ...(boundedHeight !== undefined
            ? {
                maxHeight:
                  typeof boundedHeight === "number"
                    ? `${boundedHeight}px`
                    : boundedHeight,
              }
            : null),
          ...(props.style as React.CSSProperties | undefined),
        }}
        role="article"
        aria-label={ariaLabel || title || "Detail View"}
        data-testid={dataTestId}
        {...props}
      >
        {/* Header */}
        {renderHeader()}

        {/* Content */}
        {renderContent()}
      </div>
    );
  }
);

GlassDetailView.displayName = "GlassDetailView";
